import React from 'react';

import { IAlphaPendingConfirmation, IAlphaRequestInFlight } from 'store/alpha/types';
import { pendingCardView } from 'services/alpha/pendingConfirmations';
import AlphaConfirmationCard from 'components/Alpha/AlphaConfirmationCard';
import AlphaPodCardScope from 'components/Alpha/AlphaPodCardScope';
import { SWITCHING_TEXT, blockedNoteFor } from 'components/Alpha/cardEdits';
import { AlphaAskCard } from 'components/Alpha/cards/AlphaAskCard';
import { AlphaEditablePreview } from 'components/Alpha/cards/AlphaEditablePreview';
import { AlphaFormCard } from 'components/Alpha/cards/AlphaFormCard';
import { CardServerError, CardSubmitResult, FormOption } from 'components/Alpha/cards/cardTypes';

export interface IPendingCardActions {
  cardErrors?: Record<string, CardServerError>;
  submittingTokens?: string[];
  cancellingTokens?: string[];
  requestInFlight?: IAlphaRequestInFlight;
  switching?: boolean;
  submitCard: (token: string, values: Record<string, unknown>) => Promise<CardSubmitResult>;
  cancelCard: (token: string) => Promise<void>;
  loadCardOptions: (token: string, source: string, query: string) => Promise<FormOption[]>;
}

interface IProps {
  pending: IAlphaPendingConfirmation;
  actions?: IPendingCardActions;
  onConfirm: (token: string) => Promise<boolean>;
  onCancel: (token: string) => void | Promise<void>;
}

const PendingCardBody: React.FC<IProps> = ({ pending, actions, onConfirm, onCancel }) => {
  const view = pendingCardView(pending);
  const { card, token } = pending;
  const serverError = actions?.cardErrors?.[token];
  const cancelling = actions?.cancellingTokens?.includes(token) ?? false;
  const blockedNote = actions?.switching
    ? SWITCHING_TEXT
    : blockedNoteFor(actions?.requestInFlight, token);
  const blocked = blockedNote !== undefined;
  const cancelBlocked = Boolean(actions?.switching);
  if (view === 'confirm' || !card || !actions) {
    return (
      <AlphaConfirmationCard
        confirmation={pending}
        serverError={serverError}
        blocked={blocked}
        blockedNote={blockedNote}
        cancelBlocked={cancelBlocked}
        cancelling={cancelling}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    );
  }
  const props = {
    token,
    card,
    serverError,
    submitting: actions.submittingTokens?.includes(token) ?? false,
    cancelling,
    blocked,
    blockedMessage: blockedNote,
    cancelBlocked,
    loadOptions: (source: string, query: string) => actions.loadCardOptions(token, source, query),
    onSubmit: (values: Record<string, unknown>) => actions.submitCard(token, values),
    onCancel: () => actions.cancelCard(token),
  };
  if (view === 'form') return <AlphaFormCard {...props} />;
  if (view === 'ask') return <AlphaAskCard {...props} />;
  return <AlphaEditablePreview {...props} />;
};

const AlphaPendingCard: React.FC<IProps> = props => {
  const { pending } = props;
  return (
    <AlphaPodCardScope token={pending.token}>
      <PendingCardBody {...props} />
    </AlphaPodCardScope>
  );
};

export default AlphaPendingCard;
